import React, { useEffect, useState } from 'react'

import { useMember, useTenant } from '@rewardops/auth'
import { useFilter } from '@rewardops/ui'

import { NumberInput } from '~/components/input/number-input'
import MapleButton from '~/components/maple/components/button'
import { useTranslation } from '~/app/i18n/client'
import { UserBalanceFilter } from './user-balance-filter'

export const PricePointFilter = ({ onApply }: { onApply?: Function }) => {
  const { t } = useTranslation()
  const member = useMember()
  const { minPrice, maxPrice, setMinMaxPrice } = useFilter()
  const [minValue, setMinValue] = React.useState<number>(minPrice)
  const [maxValue, setMaxValue] = React.useState<number>(maxPrice)
  const [error, setError] = useState<string | null>(null)
  const tenant = useTenant()

  const MAX_POINTS_THRESHOLD = tenant.maxFilterPoints
  // Handle minPrice, maxPrice query values updating
  useEffect(() => {
    setMinValue(minPrice)
  }, [minPrice])

  useEffect(() => {
    setMaxValue(maxPrice)
  }, [maxPrice])

  const handleMaxValueChange = (
    value: number | ((prevState: number) => number),
  ) => {
    const newValue = typeof value === 'function' ? value(maxValue) : value
    if (newValue > MAX_POINTS_THRESHOLD) {
      setError(t('filters.errors.maxExceedsThreshold'))
    } else {
      setError(null)
    }
    setMaxValue(newValue)
  }

  const updatePriceRange = () => {
    if (minValue > maxValue && maxValue !== 0) {
      setError(t('filters.errors.minExceedsMax'))
    } else if (maxValue > MAX_POINTS_THRESHOLD) {
      setError(t('filters.errors.maxExceedsThreshold'))
    } else {
      setError(null)
      setMinMaxPrice({ minPrice: minValue, maxPrice: maxValue })

      if (onApply) {
        onApply()
      }
    }
  }

  return (
    <div className="mb-5 flex flex-col gap-2">
      <div className="ml-1 mr-1 flex flex-row gap-3">
        <NumberInput
          id="point-range-min"
          name={t('filters.min')}
          error={error}
          value={minValue}
          OnChange={setMinValue}
          placeholder="0"
        />
        <NumberInput
          id="point-range-max"
          name={t('filters.max')}
          error={error}
          value={maxValue}
          OnChange={handleMaxValueChange}
          placeholder="999999"
        />
      </div>
      <MapleButton
        id="point-range-apply-button"
        buttonStyle={maxPrice || minPrice ? 'primary' : 'secondary'}
        disabled={
          (!minValue && !maxValue) ||
          maxValue > MAX_POINTS_THRESHOLD ||
          (minValue > maxValue && maxValue !== 0)
        }
        onClick={updatePriceRange}
      >
        {t('filters.apply')}
      </MapleButton>
      {member ? (
        <div className="mt-2">
          <UserBalanceFilter onApply={onApply} />
        </div>
      ) : null}
      {error && <div className="text-red-500">{error}</div>}
    </div>
  )
}

export default PricePointFilter
